import React, { FC, useState, useContext } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { LegislationLevelContentTitle } from './components/LegislationLevelComponents';
import styles from './legislation_level_block.module.scss';
import { Hyperlink } from '../../../../components/Hyperlink';
import { CodeContent } from '../legislationCode.types';
import { doctrineAnalytics } from '../../../../utils/analyticsUtils';
import { Row, Column } from '../../../../components/grid/Grid';
import { MaterialIcon } from '../../../../components/material-icon/MaterialIcon';
import { getDateKey } from '../../../../utils/date-utils/dateUtils';
import { buildUrlWithParameters } from '../../../../utils/url-utils/urlUtils';
import { UserContext } from '../../../../user/UserContext';

type Props = {
  codeContent: CodeContent;
  source: string;
  date: string;
  isCode: boolean;
  textId: string;
  url: string;
};

const LegislationLevelArticles: FC<{
  number: string;
  url: string;
  source: string;
  date: string;
}> = ({ number, source, date, url }) => {
  const today = getDateKey(new Date());
  return (
    <Hyperlink
      href={buildUrlWithParameters(
        url,
        (date && date !== today && { date }) || {},
      )}
      source={source}
    >
      <span className={styles.articleLink}>
        <FormattedMessage
          id="code/LegislationCodeLevel.tsx/1"
          values={{ number }}
        />
      </span>
    </Hyperlink>
  );
};

export const LegislationCodeLevel: FC<Props> = ({
  codeContent,
  source,
  date,
  isCode,
  textId,
  url,
}) => {
  const today = getDateKey(new Date());
  const [isToggled, setIsToggled] = useState(false);
  const {
    currentUser: { isSnapshotsCrawler },
  } = useContext(UserContext);
  const { level, title, articles, legId } = codeContent;

  const classNameRow = (rowLevel: number) => {
    if (rowLevel === 1) return styles.rowLevel1;
    if (rowLevel === 2) return styles.rowLevel2;
    return '';
  };

  const onClickArticlesRange = () => {
    setIsToggled(!isToggled);
    const analyticsName = isCode
      ? 'LegislationText SectionExpanded'
      : 'LegislationSection SectionExpanded';
    doctrineAnalytics(analyticsName, {
      expand_action: !isToggled ? 'open' : 'close',
      legislationType: 'code',
      date,
      ...(!isCode && { section_id: legId }),
      text_id: textId,
    });
  };

  if (!legId) return null;

  return (
    <Row className={classNames(styles.rowMargin, classNameRow(level))}>
      <Column small={12} medium={2}>
        {title && (
          <Hyperlink
            href={buildUrlWithParameters(url, {
              source,
              ...(date && today !== date && { dateKey: date }),
            })}
            className={styles.link}
          >
            {title[0]}
          </Hyperlink>
        )}
      </Column>
      <Column small={12} medium={8}>
        {title && (
          <LegislationLevelContentTitle
            sectionId={legId}
            level={level}
            title={title[1]}
            textId={textId}
            source={source}
            dateKey={date}
            url={url}
          >
            {!!articles.length && (
              <ol
                className={classNames(styles.articlesList, {
                  /**
                   * we want to show all the links when is the snapshot
                   * crawler because we think that Google Bots does not
                   * visit the links that are not displayed, even
                   * if the links are in the html source code
                   */
                  [styles.showArticles]: isToggled || isSnapshotsCrawler,
                })}
                data-snapshot="remove"
              >
                {articles.map(({ articleNum, articleId, url: articleUrl }) => (
                  <li key={articleId} className={styles.articleItem}>
                    <LegislationLevelArticles
                      number={articleNum}
                      url={articleUrl}
                      source={source}
                      date={date}
                    />
                  </li>
                ))}
              </ol>
            )}
          </LegislationLevelContentTitle>
        )}
      </Column>
      <Column small={12} medium={2}>
        {!!articles.length && (
          <span
            className={styles.articlesRangeBlock}
            onClick={onClickArticlesRange}
          >
            <span className={styles.articleRange}>
              {articles.length === 1 ? (
                articles[0].articleNum
              ) : (
                <FormattedMessage
                  id="code/LegislationCodeLevel.tsx/3"
                  values={{
                    articleNumberStart: articles[0].articleNum,
                    articleNumberEnd: articles[articles.length - 1].articleNum,
                  }}
                />
              )}
            </span>
            <MaterialIcon
              name="keyboard_arrow_down"
              className={classNames(styles.arrow, {
                [styles.arrowUp]: isToggled,
              })}
            />
          </span>
        )}
      </Column>
    </Row>
  );
};
